/**
 * AfterDoc 내보내기 파일 경량 탐지 헬퍼 (순수 함수, 네트워크·DOM 없음).
 *
 * 상담·고객 이관은 이제 counsel-api 가 파일을 통째로 받아 서버측에서 파싱한다.
 * (예전엔 브라우저에서 66k 메시지를 직접 파싱했으나, 81MB 전량은 서버 파싱이 맞다.)
 * 따라서 브라우저는 "이 파일에 어떤 이관 대상이 들어있는가" 만 판별하면 된다.
 *
 * data-store.json 은 `"<type>:<id>" -> { type, id, data }` 플랫 맵이다.
 * 알림톡(automation / manual)과 상담(chatroom / message / patient)이 한 파일에 섞여 있을 수 있으므로
 * 키 접두어(=type)만 세어 각 대상의 존재/규모를 알려준다.
 */

/** entry.type 이 없으면 `"<type>:<id>"` 키의 접두어를 type 으로 본다. */
function typeOf(key, entry) {
  if (entry && entry.type) return entry.type;
  return String(key).split(":")[0];
}

/**
 * data-store 에서 알림톡 / 상담 이관 대상의 규모를 센다 (키 접두어 카운트).
 * 실제 정밀 집계는 각 카드가 담당한다 — 여기서는 카드 노출 여부와 요약 스트립용 개수만 낸다.
 *
 * @param {Object} store  `"<type>:<id>" -> { type, id, data }` 맵
 * @returns {{alimtalk: {automations:number, cautions:number}, counsel: {patients:number, rooms:number, messages:number}}}
 */
export function detectTargets(store) {
  let automations = 0;
  let cautions = 0;
  let patients = 0;
  let rooms = 0;
  let messages = 0;

  for (const [key, entry] of Object.entries(store || {})) {
    switch (typeOf(key, entry)) {
      case "automation":
        automations += 1;
        break;
      // manual / manual-item / manual-folder = 주의사항 템플릿 후보
      case "manual":
      case "manual-item":
      case "manual-folder":
        cautions += 1;
        break;
      case "patient":
        patients += 1;
        break;
      case "chatroom":
        rooms += 1;
        break;
      case "message":
        messages += 1;
        break;
      default:
        break;
    }
  }

  return {
    alimtalk: { automations, cautions },
    counsel: { patients, rooms, messages },
  };
}

/** 알림톡 이관 대상(자동화 시나리오 또는 주의사항 템플릿)이 하나라도 있는가. */
export function hasAlimtalkTargets(detected) {
  return Boolean(detected && (detected.alimtalk.automations > 0 || detected.alimtalk.cautions > 0));
}

/** 상담·고객 이관 대상(고객/상담방/메시지)이 하나라도 있는가. */
export function hasCounselTargets(detected) {
  return Boolean(
    detected &&
      (detected.counsel.patients > 0 || detected.counsel.rooms > 0 || detected.counsel.messages > 0)
  );
}
